import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
   Box,
   Typography,
   Button,
   Paper,
   Grid,
   CircularProgress,
   Alert,
   IconButton,
   Divider,
   Card,
   CardContent,
   Tooltip,
   Stack,
   Badge,
   Chip,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   DialogContentText,
   Snackbar,
   Alert as MuiAlert,
} from '@mui/material';
import {
   Videocam as VideocamIcon,
   VideocamOff as VideocamOffIcon,
   Mic as MicIcon,
   MicOff as MicOffIcon,
   ScreenShare as ScreenShareIcon,
   StopScreenShare as StopScreenShareIcon,
   People as PeopleIcon,
   CallEnd as CallEndIcon,
   FiberManualRecord as RecordIcon,
   VolumeUp as VolumeUpIcon,
   VolumeOff as VolumeOffIcon,
   SignalWifi4Bar as SignalIcon,
   DragIndicator as DragIcon,
} from '@mui/icons-material';
import { useTwilioVideo, TwilioParticipant } from '../../hooks/useTwilioVideo';
import { useUser } from '../../contexts/UserContext';
import { useVideoCall } from '../../contexts/VideoCallContext';

interface TwilioVideoRoomProps {
   roomName: string;
   eventTitle: string;
   onLeave?: () => void;
   onError?: (error: Error) => void;
   showParticipants?: boolean;
   enableRecording?: boolean;
   role: string;
   isPopupMode?: boolean;
}

interface ThumbnailState {
   id: string;
   width: number;
   height: number;
   x: number;
   y: number;
   isResizing: boolean;
   isDragging: boolean;
}

const TwilioVideoRoom: React.FC<TwilioVideoRoomProps> = ({
   roomName,
   eventTitle,
   onLeave,
   onError,
   showParticipants = true,
   enableRecording = false,
   role,
   isPopupMode = false,
}) => {
   const { currentUser } = useUser();
   const [showParticipantsList, setShowParticipantsList] = useState(false);
   const [connectionQuality, setConnectionQuality] = useState<
      'excellent' | 'good' | 'fair' | 'poor'
   >('good');
   const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);

   // Thumbnail management state
   const [thumbnails, setThumbnails] = useState<{ [key: string]: ThumbnailState }>({});
   const [dragState, setDragState] = useState<{
      isDragging: boolean;
      thumbnailId: string | null;
      startX: number;
      startY: number;
      startThumbX: number;
      startThumbY: number;
   }>({
      isDragging: false,
      thumbnailId: null,
      startX: 0,
      startY: 0,
      startThumbX: 0,
      startThumbY: 0,
   });

   const mainVideoContainerRef = useRef<HTMLDivElement>(null);
   const participantVideoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});
   const thumbnailVideoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});

   // State to prevent multiple simultaneous attachment attempts
   const [attachmentInProgress, setAttachmentInProgress] = useState(false);
   const attachmentTimeoutRef = useRef<NodeJS.Timeout | null>(null);

   // Separate arrays for camera and screen share tracks
   const [cameraTracksMap, setCameraTracksMap] = useState<{ [participantId: string]: any }>({});
   const [screenShareTracksMap, setScreenShareTracksMap] = useState<{
      [participantId: string]: any;
   }>({});

   // State to track attached thumbnails to prevent reattachment
   const [attachedThumbnails, setAttachedThumbnails] = useState<Set<string>>(new Set());

   // State to track if screen share is already attached to main video
   const [isScreenShareAttached, setIsScreenShareAttached] = useState(false);
   const prevScreenSharerRef = useRef<string | null>(null);

   // Loading states for different video attachment scenarios
   const [isMainVideoLoading, setIsMainVideoLoading] = useState(false);
   const [isScreenShareLoading, setIsScreenShareLoading] = useState(false);
   const [thumbnailLoadingStates, setThumbnailLoadingStates] = useState<{ [key: string]: boolean }>(
      {}
   );
   const [loadingMessage, setLoadingMessage] = useState<string>('');

   // State to track if screen share update is in progress to prevent overlapping updates
   const [isUpdatingScreenShare, setIsUpdatingScreenShare] = useState(false);

   // Toast notification state
   const [toastOpen, setToastOpen] = useState(false);
   const [toastMessage, setToastMessage] = useState('');
   const [toastSeverity, setToastSeverity] = useState<'error' | 'warning' | 'info' | 'success'>(
      'error'
   );

   // Debug effect to log separated track arrays
   useEffect(() => {
      if (Object.keys(cameraTracksMap).length > 0 || Object.keys(screenShareTracksMap).length > 0) {
         console.log(
            `[TRACK ARRAYS DEBUG] Camera tracks:`,
            Object.keys(cameraTracksMap).map((id) => ({
               id,
               identity: cameraTracksMap[id]?.identity,
               dimensions: `${cameraTracksMap[id]?.width}x${cameraTracksMap[id]?.height}`,
               enabled: cameraTracksMap[id]?.track?.isEnabled,
            }))
         );

         console.log(
            `[TRACK ARRAYS DEBUG] Screen share tracks:`,
            Object.keys(screenShareTracksMap).map((id) => ({
               id,
               identity: screenShareTracksMap[id]?.identity,
               dimensions: `${screenShareTracksMap[id]?.width}x${screenShareTracksMap[id]?.height}`,
               enabled: screenShareTracksMap[id]?.track?.isEnabled,
            }))
         );
      }
   }, [cameraTracksMap, screenShareTracksMap]);

   // Use the simplified hook that gets data from global context
   const {
      room,
      isConnecting,
      isConnected,
      error,
      participants,
      localParticipant,
      dominantSpeaker,
      currentScreenSharer,
      isVideoEnabled,
      isAudioEnabled,
      isScreenSharing,
      isPopupMode: hookIsPopupMode,
      isMinimized: hookIsMinimized,
      connectToRoom,
      disconnectFromRoom,
      toggleVideo,
      toggleAudio,
      toggleScreenShare,
      localVideoRef,
      remoteVideoContainerRef,
      screenShareTrack,
      forceReattachThumbnails,
   } = useTwilioVideo({
      role: role as 'host' | 'participant',
      roomName,
      identity: currentUser?.displayName || currentUser?.email || 'Anonymous',
      onRoomJoined: (room) => {},
      onRoomLeft: () => {
         onLeave?.();
      },
      onParticipantConnected: (participant) => {},
      onParticipantDisconnected: (participant) => {},
      onError: (error) => {
         onError?.(error);
      },
   });

   // Get access to the video call context for enhanced functionality
   const { forceRecreateRemoteVideoElements, videoCallState } = useVideoCall();
   const localScreenShareTrack = videoCallState.screenShareTrack;

   // Initialize thumbnails for participants with camera tracks
   useEffect(() => {
      // Log when this effect runs during screen sharing
      if (currentScreenSharer) {
         console.log(
            `[THUMBNAIL CREATION] Running during screen sharing by ${currentScreenSharer.identity}`
         );
      }

      const newThumbnails: { [key: string]: ThumbnailState } = {};

      // Add thumbnails for remote participants with camera tracks
      let index = 0;
      Object.keys(cameraTracksMap).forEach((participantId) => {
         const cameraTrackInfo = cameraTracksMap[participantId];
         if (cameraTrackInfo && cameraTrackInfo.track && cameraTrackInfo.track.isEnabled) {
            // Check if thumbnail already exists - if not, create it
            setThumbnails((prevThumbnails) => {
               if (!prevThumbnails[participantId]) {
                  console.log(
                     `[THUMBNAIL CREATION] Creating thumbnail for ${cameraTrackInfo.identity} with camera track`
                  );
                  const newThumbnail = {
                     id: participantId,
                     width: 200,
                     height: 150,
                     x: 20 + index * 220, // Offset multiple thumbnails
                     y: 20,
                     isResizing: false,
                     isDragging: false,
                  };
                  return { ...prevThumbnails, [participantId]: newThumbnail };
               }
               return prevThumbnails;
            });
            index++;
         }
      });

      // Add local participant thumbnail when screen sharing (from someone else)
      // This should NOT affect the main video display
      if (
         currentScreenSharer &&
         currentScreenSharer.sid !== localParticipant?.sid &&
         isVideoEnabled
      ) {
         const localKey = 'local-video';
         setThumbnails((prevThumbnails) => {
            if (!prevThumbnails[localKey]) {
               console.log(
                  `[THUMBNAIL CREATION] Creating local video thumbnail during screen sharing by ${currentScreenSharer.identity}`
               );
               const newThumbnail = {
                  id: localKey,
                  width: 200,
                  height: 150,
                  x: 20,
                  y: 20,
                  isResizing: false,
                  isDragging: false,
               };
               return { ...prevThumbnails, [localKey]: newThumbnail };
            }
            return prevThumbnails;
         });
      }

      // Clean up thumbnails for participants who no longer have camera tracks
      setThumbnails((prevThumbnails) => {
         const updated = { ...prevThumbnails };
         const activeCameraParticipantIds = Object.keys(cameraTracksMap).filter(
            (id) =>
               cameraTracksMap[id] &&
               cameraTracksMap[id].track &&
               cameraTracksMap[id].track.isEnabled
         );

         Object.keys(updated).forEach((key) => {
            if (key !== 'local-video' && !activeCameraParticipantIds.includes(key)) {
               console.log(`[THUMBNAIL CLEANUP] Removing thumbnail for ${key}`);
               delete updated[key];
               // Also clear loading state for removed thumbnails
               setThumbnailLoadingStates((prev) => {
                  const updatedLoading = { ...prev };
                  delete updatedLoading[key];
                  return updatedLoading;
               });
               // Clear from attached thumbnails set
               setAttachedThumbnails((prev) => {
                  const newSet = new Set(prev);
                  newSet.delete(key);
                  return newSet;
               });
            }
         });

         // Remove local video thumbnail if not needed
         if (
            updated['local-video'] &&
            (!currentScreenSharer ||
               currentScreenSharer.sid === localParticipant?.sid ||
               !isVideoEnabled)
         ) {
            console.log(`[THUMBNAIL CLEANUP] Removing local video thumbnail`);
            delete updated['local-video'];
            // Also clear loading state for local video thumbnail
            setThumbnailLoadingStates((prev) => {
               const updatedLoading = { ...prev };
               delete updatedLoading['local-video'];
               return updatedLoading;
            });
            // Clear from attached thumbnails set
            setAttachedThumbnails((prev) => {
               const newSet = new Set(prev);
               newSet.delete('local-video');
               return newSet;
            });
         }

         return updated;
      });

      // REMOVED: Screen share priority enforcement that was causing flashing
   }, [cameraTracksMap, currentScreenSharer, localParticipant?.sid, isVideoEnabled]);

   // Mouse event handlers for dragging and resizing
   useEffect(() => {
      const handleMouseMove = (e: MouseEvent) => {
         if (dragState.isDragging && dragState.thumbnailId) {
            const deltaX = e.clientX - dragState.startX;
            const deltaY = e.clientY - dragState.startY;

            setThumbnails((prev) => ({
               ...prev,
               [dragState.thumbnailId!]: {
                  ...prev[dragState.thumbnailId!],
                  x: Math.max(
                     0,
                     Math.min(
                        dragState.startThumbX + deltaX,
                        window.innerWidth - prev[dragState.thumbnailId!].width
                     )
                  ),
                  y: Math.max(
                     0,
                     Math.min(
                        dragState.startThumbY + deltaY,
                        window.innerHeight - prev[dragState.thumbnailId!].height
                     )
                  ),
               },
            }));
         }
      };

      const handleMouseUp = () => {
         if (dragState.isDragging) {
            // Clear dragging state for the thumbnail
            if (dragState.thumbnailId) {
               setThumbnails((prev) => ({
                  ...prev,
                  [dragState.thumbnailId!]: {
                     ...prev[dragState.thumbnailId!],
                     isDragging: false,
                  },
               }));
            }

            setDragState({
               isDragging: false,
               thumbnailId: null,
               startX: 0,
               startY: 0,
               startThumbX: 0,
               startThumbY: 0,
            });
         }
      };

      if (dragState.isDragging) {
         document.addEventListener('mousemove', handleMouseMove);
         document.addEventListener('mouseup', handleMouseUp);
      }

      return () => {
         document.removeEventListener('mousemove', handleMouseMove);
         document.removeEventListener('mouseup', handleMouseUp);
      };
   }, [dragState]);

   // Simplified video attachment function - now uses separated track arrays
   const attachVideoToThumbnail = useCallback(
      (thumbnailEl: HTMLVideoElement, participantId: string) => {
         if (!thumbnailEl) return;

         // Check if already attached to prevent reattachment
         if (attachedThumbnails.has(participantId) && thumbnailEl.srcObject) {
            console.log(`[THUMBNAIL] Already attached for ${participantId}, skipping`);
            return;
         }

         // Check if thumbnail is currently loading - prevent overlapping calls
         if (thumbnailLoadingStates[participantId]) {
            console.log(`[THUMBNAIL] Already loading for ${participantId}, skipping`);
            return;
         }

         // Show loading state for this thumbnail only after checks
         setThumbnailLoadingStates((prev) => ({ ...prev, [participantId]: true }));

         // For local video thumbnail
         if (participantId === 'local-video') {
            console.log(`[THUMBNAIL] Attaching local video thumbnail`);

            // Always show camera feed in thumbnail, never screen share
            if (localVideoRef.current && localVideoRef.current.srcObject) {
               const localStream = localVideoRef.current.srcObject as MediaStream;
               if (localStream && thumbnailEl.srcObject !== localStream) {
                  thumbnailEl.srcObject = localStream;
                  thumbnailEl.play().catch(() => {});
                  console.log('[THUMBNAIL] Local camera feed attached to thumbnail');
                  setAttachedThumbnails((prev) => new Set(prev).add(participantId));
               }
            }

            // Hide loading state immediately
            setThumbnailLoadingStates((prev) => ({ ...prev, [participantId]: false }));
            return;
         }

         // For remote participant thumbnails - use camera tracks array
         const cameraTrackInfo = cameraTracksMap[participantId];
         if (cameraTrackInfo) {
            console.log(
               `[THUMBNAIL] Using camera track for ${cameraTrackInfo.identity} (${cameraTrackInfo.width}x${cameraTrackInfo.height})`
            );

            try {
               // Only attach if not already attached
               if (!thumbnailEl.srcObject) {
                  cameraTrackInfo.track.attach(thumbnailEl);
                  thumbnailEl.play().catch(() => {});
                  console.log(
                     `[THUMBNAIL] Camera feed attached to thumbnail for ${cameraTrackInfo.identity}`
                  );
                  setAttachedThumbnails((prev) => new Set(prev).add(participantId));
               }
            } catch (error) {
               console.warn(
                  `[THUMBNAIL] Failed to attach camera track for ${cameraTrackInfo.identity}:`,
                  error
               );
            }
         } else {
            console.log(`[THUMBNAIL] No camera track found for participant ${participantId}`);
         }

         // Hide loading state immediately
         setThumbnailLoadingStates((prev) => ({ ...prev, [participantId]: false }));
      },
      [attachedThumbnails, thumbnailLoadingStates, cameraTracksMap, localVideoRef]
   );

   // Effect to immediately handle new participants joining or video being enabled
   useEffect(() => {
      // Force immediate thumbnail attachment when camera tracks are available
      Object.keys(cameraTracksMap).forEach((participantId) => {
         const cameraTrackInfo = cameraTracksMap[participantId];
         if (cameraTrackInfo && cameraTrackInfo.track && cameraTrackInfo.track.isEnabled) {
            // Check if we have a thumbnail for this participant
            const thumbnailElement = thumbnailVideoRefs.current[participantId];
            if (thumbnailElement && !attachedThumbnails.has(participantId)) {
               console.log(
                  `[IMMEDIATE ATTACHMENT] Attaching video for ${cameraTrackInfo.identity}`
               );
               attachVideoToThumbnail(thumbnailElement, participantId);
            }
         }
      });
   }, [cameraTracksMap, attachedThumbnails]);

   // Effect to force immediate thumbnail updates when participants change
   useEffect(() => {
      console.log(
         `[PARTICIPANTS CHANGE] ${participants.length} participants, forcing thumbnail update`
      );

      // Small delay to ensure video elements are ready
      setTimeout(() => {
         Object.keys(cameraTracksMap).forEach((participantId) => {
            const cameraTrackInfo = cameraTracksMap[participantId];
            if (cameraTrackInfo && cameraTrackInfo.track && cameraTrackInfo.track.isEnabled) {
               const thumbnailElement = thumbnailVideoRefs.current[participantId];
               if (thumbnailElement) {
                  console.log(
                     `[PARTICIPANTS CHANGE] Reattaching video for ${cameraTrackInfo.identity}`
                  );
                  // Clear attached state to force reattachment
                  setAttachedThumbnails((prev) => {
                     const newSet = new Set(prev);
                     newSet.delete(participantId);
                     return newSet;
                  });
                  // Attach immediately
                  attachVideoToThumbnail(thumbnailElement, participantId);
               }
            }
         });
      }, 100); // Very short delay to ensure elements are ready
   }, [participants.length, cameraTracksMap]);

   // Effect to immediately handle video being enabled by participants
   useEffect(() => {
      console.log(`[VIDEO ENABLED] Checking for newly enabled video tracks`);

      // Force immediate attachment for all enabled camera tracks
      Object.keys(cameraTracksMap).forEach((participantId) => {
         const cameraTrackInfo = cameraTracksMap[participantId];
         if (cameraTrackInfo && cameraTrackInfo.track && cameraTrackInfo.track.isEnabled) {
            const thumbnailElement = thumbnailVideoRefs.current[participantId];
            if (thumbnailElement && !thumbnailElement.srcObject) {
               console.log(
                  `[VIDEO ENABLED] Immediately attaching video for ${cameraTrackInfo.identity}`
               );
               try {
                  cameraTrackInfo.track.attach(thumbnailElement);
                  thumbnailElement.play().catch(() => {});
                  setAttachedThumbnails((prev) => new Set(prev).add(participantId));
               } catch (error) {
                  console.warn(`[VIDEO ENABLED] Failed to attach:`, error);
                  // Fallback to normal attachment
                  attachVideoToThumbnail(thumbnailElement, participantId);
               }
            }
         }
      });
   }, [cameraTracksMap]);

   // Effect to handle video reattachment when switching between popup and full mode
   useEffect(() => {
      if (isVideoEnabled && isConnected && localVideoRef.current) {
         // Add a delay to ensure the video element is ready after mode switch
         const timer = setTimeout(() => {
            if (localVideoRef.current) {
               const videoElement = localVideoRef.current;
               const hasContent =
                  videoElement.srcObject &&
                  videoElement.videoWidth > 0 &&
                  videoElement.videoHeight > 0;

               if (!hasContent) {
                  // Force video element properties
                  videoElement.autoplay = true;
                  videoElement.playsInline = true;
                  videoElement.muted = true;

                  // Force play
                  const playPromise = videoElement.play();
                  if (playPromise !== undefined) {
                     playPromise.catch((err) => {
                        // Only log if it's not an abort error from previous play attempts
                        if (err.name !== 'AbortError') {
                           console.log('Video play failed after mode switch:', err);
                        }
                     });
                  }
               }
            }
         }, 3000);

         return () => clearTimeout(timer);
      }
   }, [isPopupMode, isConnected, currentScreenSharer, localParticipant?.sid]);

   // Effect to handle video reattachment after popup resize
   useEffect(() => {
      if (isVideoEnabled && isConnected && localVideoRef.current) {
         // Add a delay to ensure the video element is ready after resize
         const timer = setTimeout(() => {
            if (localVideoRef.current) {
               const videoElement = localVideoRef.current;
               const hasContent =
                  videoElement.srcObject &&
                  videoElement.videoWidth > 0 &&
                  videoElement.videoHeight > 0;

               if (!hasContent) {
                  // Force video element properties
                  videoElement.autoplay = true;
                  videoElement.playsInline = true;
                  videoElement.muted = true;

                  // Force play
                  const playPromise = videoElement.play();
                  if (playPromise !== undefined) {
                     playPromise.catch((err) => {
                        // Only log if it's not an abort error from previous play attempts
                        if (err.name !== 'AbortError') {
                           console.log('Video play failed after resize:', err);
                        }
                     });
                  }
               }
            }
         }, 2000);

         return () => clearTimeout(timer);
      }
   }, [localVideoRef.current, isVideoEnabled, isConnected]);

   // Effect to handle initial mount when screen sharing is already active (navigation case)
   useEffect(() => {
      // Handle case where component mounts and screen sharing is already active
      if (
         currentScreenSharer &&
         currentScreenSharer.sid !== localParticipant?.sid &&
         isConnected &&
         screenShareTracksMap[currentScreenSharer.sid]
      ) {
         console.log(
            `[INITIAL MOUNT] Screen sharing active on mount - ${currentScreenSharer.identity}`
         );
         console.log(`[INITIAL MOUNT] Mode: ${isPopupMode ? 'popup' : 'full'}`);

         // Check if screen sharing is already properly attached
         if (isScreenShareAlreadyAttached()) {
            console.log(
               `[INITIAL MOUNT] Screen sharing already attached for ${currentScreenSharer.identity}, skipping`
            );
            return;
         }

         // Show loading immediately
         setIsScreenShareLoading(true);
         setLoadingMessage(`Loading ${currentScreenSharer.identity}'s screen share...`);

         // Reduced delay for faster initial display
         const timer = setTimeout(async () => {
            console.log(
               `[INITIAL MOUNT] Attempting screen share attachment for ${currentScreenSharer.identity}`
            );
            const success = await attachScreenShareToMain();
            if (success) {
               console.log(
                  `[INITIAL MOUNT] Screen share attachment successful for ${currentScreenSharer.identity}`
               );
            } else {
               console.log(
                  `[INITIAL MOUNT] Screen share attachment failed for ${currentScreenSharer.identity}`
               );
            }
         }, 1000); // Reduced from 2000ms to 1000ms

         return () => {
            clearTimeout(timer);
            // Clean up loading state on unmount
            setIsScreenShareLoading(false);
            setLoadingMessage('');
         };
      }
   }, [
      currentScreenSharer?.sid,
      localParticipant?.sid,
      isConnected,
      screenShareTracksMap,
      isPopupMode,
   ]);

   // Effect to handle new participants joining during active screen sharing
   useEffect(() => {
      // When a new participant joins and screen sharing is already active, immediately show screen share
      if (
         currentScreenSharer &&
         currentScreenSharer.sid !== localParticipant?.sid &&
         isConnected &&
         screenShareTracksMap[currentScreenSharer.sid] &&
         localParticipant?.sid // Ensure we have local participant info
      ) {
         console.log(
            `[NEW PARTICIPANT] Joining with active screen sharing from ${currentScreenSharer.identity}`
         );

         // Check if screen sharing is already properly attached
         if (isScreenShareAlreadyAttached()) {
            console.log(
               `[NEW PARTICIPANT] Screen sharing already attached for ${currentScreenSharer.identity}, skipping`
            );
            return;
         }

         // Show loading immediately
         setIsScreenShareLoading(true);
         setLoadingMessage(`Joining ${currentScreenSharer.identity}'s screen share...`);

         // Very short delay for immediate display to new participants
         const timer = setTimeout(async () => {
            console.log(
               `[NEW PARTICIPANT] Immediately attaching screen share for ${currentScreenSharer.identity}`
            );
            const success = await attachScreenShareToMain();
            if (success) {
               console.log(
                  `[NEW PARTICIPANT] Screen share attachment successful for ${currentScreenSharer.identity}`
               );
            } else {
               console.log(
                  `[NEW PARTICIPANT] Screen share attachment failed for ${currentScreenSharer.identity}`
               );
            }
         }, 500); // Very short delay for immediate response

         return () => {
            clearTimeout(timer);
            // Clean up loading state on unmount
            setIsScreenShareLoading(false);
            setLoadingMessage('');
         };
      }
   }, [localParticipant?.sid, currentScreenSharer?.sid, isConnected, screenShareTracksMap]);

   // Effect to handle screen share reattachment when transitioning from popup to full mode
   useEffect(() => {
      // Only handle remote screen sharing during popup to full mode transition
      if (
         currentScreenSharer &&
         currentScreenSharer.sid !== localParticipant?.sid &&
         !isPopupMode && // Component is in full mode
         isConnected
      ) {
         console.log(
            `[MODE TRANSITION] Full mode active - reattaching screen share for ${currentScreenSharer.identity}`
         );
         console.log(
            `[MODE TRANSITION] Hook state: isPopupMode=${isPopupMode}, hookIsPopupMode=${hookIsPopupMode}`
         );

         // Check if screen sharing is already properly attached
         if (isScreenShareAlreadyAttached()) {
            console.log(
               `[MODE TRANSITION] Screen sharing already attached for ${currentScreenSharer.identity}, skipping`
            );
            return;
         }

         // Show loading immediately
         setIsScreenShareLoading(true);
         setLoadingMessage(`Switching to full mode...`);

         // Reduced delay for faster transitions
         const timer = setTimeout(async () => {
            console.log(
               `[MODE TRANSITION] Attempting screen share reattachment for ${currentScreenSharer.identity}`
            );
            const success = await attachScreenShareToMain();
            if (success) {
               console.log(
                  `[MODE TRANSITION] Screen share reattachment successful for ${currentScreenSharer.identity}`
               );
            } else {
               console.log(
                  `[MODE TRANSITION] Screen share reattachment failed for ${currentScreenSharer.identity}`
               );
            }
         }, 800); // Reduced from 1500ms to 800ms

         return () => {
            clearTimeout(timer);
            // Clean up loading state on unmount
            setIsScreenShareLoading(false);
            setLoadingMessage('');
         };
      }
   }, [isPopupMode, hookIsPopupMode, currentScreenSharer?.sid, localParticipant?.sid, isConnected]);

   // Effect to handle screen share reattachment during popup mode minimize/maximize
   useEffect(() => {
      // Only handle remote screen sharing during popup mode changes
      if (
         currentScreenSharer &&
         currentScreenSharer.sid !== localParticipant?.sid &&
         isPopupMode && // In popup mode (prop)
         hookIsPopupMode && // Hook also shows popup mode
         isConnected
      ) {
         console.log(
            `[POPUP TRANSITION] Popup mode change - reattaching screen share for ${currentScreenSharer.identity}`
         );
         console.log(
            `[POPUP TRANSITION] State: isMinimized=${hookIsMinimized}, isPopupMode=${isPopupMode}, hookIsPopupMode=${hookIsPopupMode}`
         );

         // Check if screen sharing is already properly attached
         if (isScreenShareAlreadyAttached()) {
            console.log(
               `[POPUP TRANSITION] Screen sharing already attached for ${currentScreenSharer.identity}, skipping`
            );
            return;
         }

         // Show loading immediately
         setIsScreenShareLoading(true);
         setLoadingMessage(`Restoring screen share...`);

         // Reduced delay for faster popup transitions
         const timer = setTimeout(async () => {
            console.log(
               `[POPUP TRANSITION] Attempting screen share reattachment for ${currentScreenSharer.identity}`
            );
            const success = await attachScreenShareToMain();
            if (success) {
               console.log(
                  `[POPUP TRANSITION] Screen share reattachment successful for ${currentScreenSharer.identity}`
               );
            } else {
               console.log(
                  `[POPUP TRANSITION] Screen share reattachment failed for ${currentScreenSharer.identity}`
               );
            }
         }, 600); // Reduced from 1000ms to 600ms

         return () => {
            clearTimeout(timer);
            // Clean up loading state on unmount
            setIsScreenShareLoading(false);
            setLoadingMessage('');
         };
      }
   }, [
      isPopupMode,
      hookIsPopupMode,
      hookIsMinimized,
      currentScreenSharer?.sid,
      localParticipant?.sid,
      isConnected,
   ]);

   // Effect to handle screen share reattachment when video call context changes
   useEffect(() => {
      // Only handle remote screen sharing when there are context changes
      if (currentScreenSharer && currentScreenSharer.sid !== localParticipant?.sid && isConnected) {
         console.log(
            `[CONTEXT CHANGE] Video call context changed - checking screen share for ${currentScreenSharer.identity}`
         );
         console.log(
            `[CONTEXT CHANGE] State: isMinimized=${hookIsMinimized}, isPopupMode=${isPopupMode}, hookIsPopupMode=${hookIsPopupMode}`
         );

         // Check if screen sharing is already properly attached
         if (isScreenShareAlreadyAttached()) {
            console.log(
               `[CONTEXT CHANGE] Screen sharing already attached for ${currentScreenSharer.identity}, skipping`
            );
            return;
         }

         // Keep existing delay for context changes
         const timer = setTimeout(async () => {
            const mainVideo = document.querySelector(
               '[data-testid="main-video"]'
            ) as HTMLVideoElement;

            // Only reattach if main video is missing screen share content
            if (mainVideo) {
               const hasValidScreenShare =
                  mainVideo.srcObject &&
                  mainVideo.videoWidth > 0 &&
                  mainVideo.videoHeight > 0 &&
                  mainVideo.getAttribute('data-priority') === 'screen-share';

               if (!hasValidScreenShare) {
                  console.log(
                     `[CONTEXT CHANGE] Main video missing screen share - reattaching for ${currentScreenSharer.identity}`
                  );

                  // Show loading only when we actually need to reattach
                  setIsScreenShareLoading(true);
                  setLoadingMessage(`Reconnecting screen share...`);

                  const success = await attachScreenShareToMain();
                  if (success) {
                     console.log(
                        `[CONTEXT CHANGE] Screen share reattachment successful for ${currentScreenSharer.identity}`
                     );
                  } else {
                     console.log(
                        `[CONTEXT CHANGE] Screen share reattachment failed for ${currentScreenSharer.identity}`
                     );
                  }
               } else {
                  console.log(
                     `[CONTEXT CHANGE] Screen share already properly attached for ${currentScreenSharer.identity}`
                  );
               }
            }
         }, 800); // Keep existing 800ms delay for context changes

         return () => {
            clearTimeout(timer);
            // Clean up loading state on unmount
            setIsScreenShareLoading(false);
            setLoadingMessage('');
         };
      }
   }, [
      forceRecreateRemoteVideoElements,
      currentScreenSharer?.sid,
      localParticipant?.sid,
      isConnected,
      hookIsMinimized,
   ]);

   // Safety net effect: Ensure screen sharing is attached whenever tracks are ready
   useEffect(() => {
      if (
         currentScreenSharer &&
         currentScreenSharer.sid !== localParticipant?.sid &&
         isConnected &&
         screenShareTracksMap[currentScreenSharer.sid]
      ) {
         console.log(`[SAFETY NET] Screen share tracks ready for ${currentScreenSharer.identity}`);

         // Check if screen sharing is already properly attached
         if (isScreenShareAlreadyAttached()) {
            console.log(
               `[SAFETY NET] Screen sharing already attached for ${currentScreenSharer.identity}, skipping`
            );
            return;
         }

         // Reduced delay for faster safety net response
         const timer = setTimeout(async () => {
            const mainVideo = document.querySelector(
               '[data-testid="main-video"]'
            ) as HTMLVideoElement;

            if (mainVideo) {
               const hasValidScreenShare =
                  mainVideo.srcObject &&
                  mainVideo.videoWidth > 0 &&
                  mainVideo.videoHeight > 0 &&
                  mainVideo.getAttribute('data-priority') === 'screen-share';

               if (!hasValidScreenShare) {
                  console.log(
                     `[SAFETY NET] Main video needs screen share - attaching for ${currentScreenSharer.identity}`
                  );

                  // Show loading only when we actually need to attach
                  setIsScreenShareLoading(true);
                  setLoadingMessage(`Ensuring screen share connection...`);

                  const success = await attachScreenShareToMain();
                  if (success) {
                     console.log(
                        `[SAFETY NET] Screen share attachment successful for ${currentScreenSharer.identity}`
                     );
                  } else {
                     console.log(
                        `[SAFETY NET] Screen share attachment failed for ${currentScreenSharer.identity}`
                     );
                  }
               } else {
                  console.log(
                     `[SAFETY NET] Screen share already properly attached for ${currentScreenSharer.identity}`
                  );
               }
            }
         }, 700); // Reduced from 1000ms to 700ms

         return () => {
            clearTimeout(timer);
            // Clean up loading state on unmount
            setIsScreenShareLoading(false);
            setLoadingMessage('');
         };
      }
   }, [currentScreenSharer?.sid, localParticipant?.sid, isConnected, screenShareTracksMap]);

   // Effect to monitor screen sharing changes and ensure proper video display
   useEffect(() => {
      console.log('Screen sharing state changed:', {
         currentScreenSharer: currentScreenSharer?.identity,
         isScreenSharing,
         localParticipant: localParticipant?.identity,
         error: error,
      });

      // When screen sharing stops (currentScreenSharer becomes null), ensure local video is working
      if (!currentScreenSharer && isVideoEnabled && localVideoRef.current) {
         console.log('Screen sharing stopped - ensuring local video continues working');

         setTimeout(() => {
            if (localVideoRef.current && isVideoEnabled) {
               const videoElement = localVideoRef.current;
               const hasContent =
                  videoElement.srcObject &&
                  videoElement.videoWidth > 0 &&
                  videoElement.videoHeight > 0;

               if (!hasContent) {
                  console.log('Reattaching local video after screen sharing stopped');
                  // Force video element properties
                  videoElement.autoplay = true;
                  videoElement.playsInline = true;
                  videoElement.muted = true;

                  // Force play
                  const playPromise = videoElement.play();
                  if (playPromise !== undefined) {
                     playPromise.catch((err) => {
                        // Only log if it's not an abort error from previous play attempts
                        if (err.name !== 'AbortError') {
                           console.log('Video play failed after screen sharing stopped:', err);
                        }
                     });
                  }
               }
            }
         }, 1000); // Reduced delay for faster recovery
      }

      // Handle screen sharing errors that might affect local video
      if (error && error.includes('screen sharing') && isVideoEnabled && localVideoRef.current) {
         console.log('Screen sharing error detected - ensuring local video continues working');

         setTimeout(() => {
            if (localVideoRef.current && isVideoEnabled) {
               const videoElement = localVideoRef.current;
               const hasContent =
                  videoElement.srcObject &&
                  videoElement.videoWidth > 0 &&
                  videoElement.videoHeight > 0;

               if (!hasContent) {
                  console.log('Reattaching local video after screen sharing error');
                  // Force video element properties
                  videoElement.autoplay = true;
                  videoElement.playsInline = true;
                  videoElement.muted = true;

                  // Force play
                  const playPromise = videoElement.play();
                  if (playPromise !== undefined) {
                     playPromise.catch((err) => {
                        // Only log if it's not an abort error from previous play attempts
                        if (err.name !== 'AbortError') {
                           console.log('Video play failed after screen sharing error:', err);
                        }
                     });
                  }
               }
            }
         }, 500); // Quick recovery for errors
      }

      // When screen sharing starts, ensure we have the proper video elements
      if (currentScreenSharer) {
         console.log(`Screen sharing active by ${currentScreenSharer.identity}`);

         // If it's local screen sharing, ensure we have the screen share track
         if (currentScreenSharer.sid === localParticipant?.sid && !screenShareTrack) {
            console.warn('Local screen sharing detected but no screenShareTrack available');
         }

         // If it's remote screen sharing, ensure we have the video element
         if (currentScreenSharer.sid !== localParticipant?.sid) {
            const remoteVideoElement = document.getElementById(
               `participant-${currentScreenSharer.sid}-video`
            );
            if (!remoteVideoElement) {
               console.warn(
                  `Remote screen sharing detected but no video element found for ${currentScreenSharer.identity}`
               );
            }
         }
      }

      // Force thumbnail reattachment when screen sharing state changes
      if (Object.keys(thumbnails).length > 0) {
         setTimeout(() => {
            forceReattachThumbnails();
         }, 2000);
      }
   }, [
      currentScreenSharer,
      isScreenSharing,
      localParticipant?.sid,
      isVideoEnabled,
      screenShareTrack,
      thumbnails,
      forceReattachThumbnails,
      error, // Added error to dependencies
   ]);

   // Effect to handle screen sharing permission errors as toasts
   useEffect(() => {
      // Handle screen sharing permission errors specifically
      if (error && error.toLowerCase().includes('screen sharing')) {
         console.log('Screen sharing error detected:', error);

         // Show toast notification instead of blocking the UI
         setToastMessage(error);
         setToastSeverity('error');
         setToastOpen(true);

         // Ensure local video continues working despite screen sharing errors
         if (isVideoEnabled && localVideoRef.current) {
            const videoElement = localVideoRef.current;
            const hasContent =
               videoElement.srcObject &&
               videoElement.videoWidth > 0 &&
               videoElement.videoHeight > 0;

            if (!hasContent) {
               console.log('Restoring local video after screen sharing error');

               // Force video element properties
               videoElement.autoplay = true;
               videoElement.playsInline = true;
               videoElement.muted = true;

               // Force play
               const playPromise = videoElement.play();
               if (playPromise !== undefined) {
                  playPromise.catch((err) => {
                     if (err.name !== 'AbortError') {
                        console.log('Video play failed during screen sharing error recovery:', err);
                     }
                  });
               }
            }
         }
      }
   }, [error, isVideoEnabled, localVideoRef.current]);

   // Effect to listen for custom screen sharing error events
   useEffect(() => {
      const handleScreenShareError = (event: CustomEvent) => {
         const { message, severity } = event.detail;
         console.log('Screen sharing error event received:', message, severity);

         // Show toast notification
         setToastMessage(message);
         setToastSeverity(severity);
         setToastOpen(true);

         // Ensure local video continues working despite screen sharing errors
         if (isVideoEnabled && localVideoRef.current) {
            const videoElement = localVideoRef.current;
            const hasContent =
               videoElement.srcObject &&
               videoElement.videoWidth > 0 &&
               videoElement.videoHeight > 0;

            if (!hasContent) {
               console.log('Restoring local video after screen sharing error event');

               // Force video element properties
               videoElement.autoplay = true;
               videoElement.playsInline = true;
               videoElement.muted = true;

               // Force play
               const playPromise = videoElement.play();
               if (playPromise !== undefined) {
                  playPromise.catch((err) => {
                     if (err.name !== 'AbortError') {
                        console.log('Video play failed during screen sharing error recovery:', err);
                     }
                  });
               }
            }
         }
      };

      // Add event listener for screen sharing errors
      window.addEventListener('screenShareError', handleScreenShareError as EventListener);

      // Cleanup event listener on unmount
      return () => {
         window.removeEventListener('screenShareError', handleScreenShareError as EventListener);
      };
   }, [isVideoEnabled, localVideoRef.current]);

   // Effect to listen for custom screen sharing success events
   useEffect(() => {
      const handleScreenShareSuccess = (event: CustomEvent) => {
         const { message, severity } = event.detail;
         console.log('Screen sharing success event received:', message);

         // Show success toast notification
         setToastMessage(message);
         setToastSeverity('success');
         setToastOpen(true);
      };

      // Add event listener for screen sharing success
      window.addEventListener('screenShareSuccess', handleScreenShareSuccess as EventListener);

      // Cleanup event listener on unmount
      return () => {
         window.removeEventListener(
            'screenShareSuccess',
            handleScreenShareSuccess as EventListener
         );
      };
   }, []);

   // REMOVED: All interval-based monitoring effects that were causing flashing

   // Helper function to check if screen sharing is already properly attached
   const isScreenShareAlreadyAttached = () => {
      if (!currentScreenSharer || currentScreenSharer.sid === localParticipant?.sid) {
         return false;
      }

      const mainVideo = document.querySelector('[data-testid="main-video"]') as HTMLVideoElement;
      if (!mainVideo) return false;

      const hasValidScreenShare =
         mainVideo.srcObject &&
         mainVideo.videoWidth > 0 &&
         mainVideo.videoHeight > 0 &&
         mainVideo.getAttribute('data-priority') === 'screen-share';

      // If screen sharing is already attached, clear any existing loading state
      if (hasValidScreenShare && isScreenShareLoading) {
         setIsScreenShareLoading(false);
         setLoadingMessage('');
      }

      return hasValidScreenShare;
   };

   // Simplified screen sharing attachment helper - now uses separated track arrays
   const attachScreenShareToMain = () => {
      return new Promise<boolean>((resolve) => {
         if (!currentScreenSharer || currentScreenSharer.sid === localParticipant?.sid) {
            // Clear loading state immediately if no screen sharer
            setIsScreenShareLoading(false);
            setLoadingMessage('');
            resolve(false);
            return;
         }

         // PROTECTION: Prevent multiple simultaneous attachment attempts
         if (attachmentInProgress) {
            console.log(
               `[SCREEN SHARE] Attachment already in progress for ${currentScreenSharer.identity}, skipping`
            );
            resolve(false);
            return;
         }

         // Show loading state
         setIsScreenShareLoading(true);
         setLoadingMessage(`Connecting to ${currentScreenSharer.identity}'s screen share...`);

         setAttachmentInProgress(true);

         // Clear any existing timeout
         if (attachmentTimeoutRef.current) {
            clearTimeout(attachmentTimeoutRef.current);
         }

         // Set timeout to reset the flag - reduced to 1 second for mode transitions
         attachmentTimeoutRef.current = setTimeout(() => {
            setAttachmentInProgress(false);
            setIsScreenShareLoading(false);
            setLoadingMessage('');
         }, 1000); // 1 second protection window

         console.log(
            `[SCREEN SHARE] Attempting to attach for ${currentScreenSharer.identity} in ${isPopupMode ? 'popup' : 'full'} mode`
         );

         // Step 1: Find main video element
         const mainVideoElement = document.querySelector(
            '[data-testid="main-video"]'
         ) as HTMLVideoElement;
         if (!mainVideoElement) {
            console.log(`[SCREEN SHARE] Main video element not found`);

            // Reset attachment protection flag and loading state on failure
            setAttachmentInProgress(false);
            setIsScreenShareLoading(false);
            setLoadingMessage('');
            if (attachmentTimeoutRef.current) {
               clearTimeout(attachmentTimeoutRef.current);
            }

            resolve(false);
            return;
         }

         console.log(`[SCREEN SHARE] Main video element found:`, {
            hasStream: !!mainVideoElement.srcObject,
            videoWidth: mainVideoElement.videoWidth,
            videoHeight: mainVideoElement.videoHeight,
         });

         // Step 2: Use screen share tracks array instead of searching
         const screenShareTrackInfo = screenShareTracksMap[currentScreenSharer.sid];

         console.log(
            `[SCREEN SHARE] Looking for screen share track for ${currentScreenSharer.identity}:`,
            {
               currentScreenSharerId: currentScreenSharer.sid,
               availableScreenShareTracks: Object.keys(screenShareTracksMap),
               foundTrack: !!screenShareTrackInfo,
               trackInfo: screenShareTrackInfo
                  ? {
                       identity: screenShareTrackInfo.identity,
                       dimensions: `${screenShareTrackInfo.width}x${screenShareTrackInfo.height}`,
                       enabled: screenShareTrackInfo.track?.isEnabled,
                    }
                  : null,
            }
         );

         if (screenShareTrackInfo) {
            console.log(
               `[SCREEN SHARE] Using screen share track for ${screenShareTrackInfo.identity} (${screenShareTrackInfo.width}x${screenShareTrackInfo.height})`
            );

            try {
               // Create a temporary video element to get the stream
               const tempVideo = document.createElement('video');
               tempVideo.style.display = 'none';
               tempVideo.style.position = 'absolute';
               tempVideo.style.top = '-1000px';
               document.body.appendChild(tempVideo);

               screenShareTrackInfo.track.attach(tempVideo);
               tempVideo.autoplay = true;
               tempVideo.playsInline = true;
               tempVideo.muted = false;

               // Wait for stream to be ready
               setTimeout(() => {
                  if (tempVideo.srcObject) {
                     const stream = tempVideo.srcObject as MediaStream;

                     // Check if main video already has the correct stream
                     if (mainVideoElement.srcObject !== stream) {
                        console.log(`[SCREEN SHARE] Attaching screen share stream to main video`);
                        setLoadingMessage(`Displaying ${currentScreenSharer.identity}'s screen...`);

                        // Attach the stream
                        mainVideoElement.srcObject = stream;
                        mainVideoElement.autoplay = true;
                        mainVideoElement.playsInline = true;
                        mainVideoElement.muted = false;

                        // Force video to be visible and active
                        mainVideoElement.style.opacity = '1';
                        mainVideoElement.style.visibility = 'visible';
                        mainVideoElement.style.display = 'block';

                        // Play the video with multiple attempts
                        const playVideo = async () => {
                           try {
                              await mainVideoElement.play();
                              console.log(`[SCREEN SHARE] Video play successful`);

                              // Force a repaint to ensure video is displayed
                              mainVideoElement.style.transform = 'translateZ(0)';
                              setTimeout(() => {
                                 mainVideoElement.style.transform = '';
                              }, 100);
                           } catch (error: any) {
                              if (error.name !== 'AbortError') {
                                 console.log(`[SCREEN SHARE] Play failed:`, error);
                                 // Try again after a short delay
                                 setTimeout(() => playVideo(), 100);
                              }
                           }
                        };

                        playVideo();

                        console.log(`[SCREEN SHARE] Screen share stream attached successfully`);

                        // Reset attachment protection flag and loading state on success
                        setAttachmentInProgress(false);
                        setIsScreenShareLoading(false);
                        setLoadingMessage('');
                        if (attachmentTimeoutRef.current) {
                           clearTimeout(attachmentTimeoutRef.current);
                        }

                        // Clean up temp video
                        document.body.removeChild(tempVideo);

                        resolve(true);
                     } else {
                        console.log(`[SCREEN SHARE] Stream already attached correctly`);

                        // Reset attachment protection flag and loading state on success
                        setAttachmentInProgress(false);
                        setIsScreenShareLoading(false);
                        setLoadingMessage('');
                        if (attachmentTimeoutRef.current) {
                           clearTimeout(attachmentTimeoutRef.current);
                        }

                        // Clean up temp video
                        document.body.removeChild(tempVideo);

                        resolve(true);
                     }
                  } else {
                     console.log(`[SCREEN SHARE] Temp video has no stream after attachment`);

                     // Reset attachment protection flag and loading state on failure
                     setAttachmentInProgress(false);
                     setIsScreenShareLoading(false);
                     setLoadingMessage('');
                     if (attachmentTimeoutRef.current) {
                        clearTimeout(attachmentTimeoutRef.current);
                     }

                     // Clean up temp video
                     document.body.removeChild(tempVideo);

                     resolve(false);
                  }
               }, 300);
            } catch (error) {
               console.log(`[SCREEN SHARE] Failed to attach screen share track:`, error);

               // Reset attachment protection flag and loading state on failure
               setAttachmentInProgress(false);
               setIsScreenShareLoading(false);
               setLoadingMessage('');
               if (attachmentTimeoutRef.current) {
                  clearTimeout(attachmentTimeoutRef.current);
               }

               resolve(false);
            }
         } else {
            console.log(
               `[SCREEN SHARE] No screen share track found for ${currentScreenSharer.identity}`
            );

            // Reset attachment protection flag and loading state on failure
            setAttachmentInProgress(false);
            setIsScreenShareLoading(false);
            setLoadingMessage('');
            if (attachmentTimeoutRef.current) {
               clearTimeout(attachmentTimeoutRef.current);
            }

            resolve(false);
         }
      });
   };

   // Enhanced main video rendering with screen share support
   const renderMainVideo = () => {
      return (
         <Box
            ref={mainVideoContainerRef}
            sx={{ position: 'relative', width: '100%', height: '100%' }}
         >
            {/* Main video container */}
            <Box
               sx={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
               }}
            >
               {currentScreenSharer ? (
                  // MAIN VIDEO: Show screen share content when someone is sharing their screen
                  // This has ABSOLUTE PRIORITY - camera states should never interfere
                  <Box
                     key={`screen-share-container-${currentScreenSharer.sid}-${isPopupMode}`}
                     sx={{ width: '100%', height: '100%', position: 'relative' }}
                  >
                     <video
                        key={`screen-share-${currentScreenSharer.sid}-${currentScreenSharer.identity}`}
                        data-testid='main-video'
                        data-screen-sharer={currentScreenSharer.identity}
                        data-popup-mode={isPopupMode}
                        data-priority='screen-share'
                        ref={(el) => {
                           if (el && currentScreenSharer) {
                              // Check if this is local screen sharing
                              const isLocalScreenShare =
                                 currentScreenSharer.sid === localParticipant?.sid;

                              if (isLocalScreenShare && screenShareTrack) {
                                 // For local screen share, use the screenShareTrack directly
                                 try {
                                    // Only attach if not already attached
                                    if (!el.srcObject) {
                                       screenShareTrack.attach(el);
                                       console.log('Local screen share attached to main video');
                                    }
                                 } catch (attachError) {
                                    console.error(
                                       'Failed to attach local screen share:',
                                       attachError
                                    );
                                 }
                              } else {
                                 // For remote screen share, use one-time attachment only
                                 const attachRemoteScreenShare = () => {
                                    // Check if screen share is already attached to prevent reattachment on every render
                                    if (isScreenShareAttached && el.srcObject) {
                                       console.log(
                                          `[SCREEN SHARE PRIORITY] Screen share already attached for ${currentScreenSharer.identity}, skipping`
                                       );
                                       return true;
                                    }

                                    console.log(
                                       `[SCREEN SHARE PRIORITY] Attempting remote screen share attachment for ${currentScreenSharer.identity}${isPopupMode ? ' [POPUP MODE]' : ''}`
                                    );

                                    // Try direct element lookup first
                                    const screenShareElement = document.getElementById(
                                       `participant-${currentScreenSharer.sid}-video`
                                    );

                                    if (
                                       screenShareElement &&
                                       screenShareElement instanceof HTMLVideoElement
                                    ) {
                                       const stream = screenShareElement.srcObject;
                                       if (stream) {
                                          console.log(
                                             `[SCREEN SHARE PRIORITY] Found direct screen share element for ${currentScreenSharer.identity}${isPopupMode ? ' [POPUP MODE]' : ''}`
                                          );

                                          // FORCE attachment - screen share has absolute priority
                                          if (el.srcObject !== stream) {
                                             el.srcObject = stream;
                                          } // Only attach if different stream
                                          el.autoplay = true;
                                          el.playsInline = true;
                                          el.muted = false;

                                          // Handle play promise properly to avoid conflicts
                                          const playPromise = el.play();
                                          if (playPromise !== undefined) {
                                             playPromise.catch((error) => {
                                                // Only log if it's not an abort error from previous play attempts
                                                if (error.name !== 'AbortError') {
                                                   console.log(
                                                      '[SCREEN SHARE PRIORITY] Remote screen share play failed:',
                                                      error
                                                   );
                                                }
                                             });
                                          }

                                          console.log(
                                             `[SCREEN SHARE PRIORITY] Remote screen share from ${currentScreenSharer.identity} attached to main video (direct)${isPopupMode ? ' [POPUP MODE]' : ''}`
                                          );

                                          // Mark screen share as attached
                                          setIsScreenShareAttached(true);

                                          return true;
                                       }
                                    }

                                    return false;
                                 };

                                 // Try attachment immediately, only once
                                 attachRemoteScreenShare();
                              }
                           }
                        }}
                        autoPlay
                        playsInline
                        muted={currentScreenSharer.sid === localParticipant?.sid}
                        style={{
                           width: '100%',
                           height: '100%',
                           objectFit: 'contain',
                           backgroundColor: '#000',
                           position: 'absolute',
                           top: 0,
                           left: 0,
                        }}
                     />

                     {/* Screen Share Loading Overlay */}
                     {isScreenShareLoading && (
                        <Box
                           sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 1000,
                           }}
                        >
                           <CircularProgress
                              sx={{
                                 color: 'white',
                                 mb: 2,
                              }}
                              size={isPopupMode ? 40 : 60}
                           />
                           <Typography
                              variant={isPopupMode ? 'body2' : 'h6'}
                              sx={{
                                 color: 'white',
                                 textAlign: 'center',
                                 px: 2,
                              }}
                           >
                              {loadingMessage || 'Loading screen share...'}
                           </Typography>
                        </Box>
                     )}

                     <Typography
                        variant='h6'
                        sx={{
                           position: 'absolute',
                           top: 16,
                           left: 16,
                           color: 'white',
                           backgroundColor: 'rgba(0,0,0,0.7)',
                           padding: '8px 16px',
                           borderRadius: '8px',
                           fontSize: isPopupMode ? '12px' : '14px',
                           fontWeight: 600,
                           zIndex: 10,
                        }}
                     >
                        🖥️ {currentScreenSharer.identity} is sharing their screen
                        {currentScreenSharer.sid === localParticipant?.sid && ' (You)'}
                     </Typography>
                  </Box>
               ) : isVideoEnabled ? (
                  // MAIN VIDEO: Show local camera feed ONLY when no screen share is active
                  <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                     <video
                        ref={localVideoRef}
                        data-testid='main-video'
                        data-priority='local-camera'
                        autoPlay
                        muted
                        playsInline
                        style={{
                           width: '100%',
                           height: '100%',
                           objectFit: 'cover',
                           transform: 'scaleX(-1)', // Mirror effect for local video
                        }}
                     />

                     {/* Main Video Loading Overlay */}
                     {isMainVideoLoading && (
                        <Box
                           sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 1000,
                           }}
                        >
                           <CircularProgress
                              sx={{
                                 color: 'white',
                                 mb: 2,
                              }}
                              size={isPopupMode ? 40 : 60}
                           />
                           <Typography
                              variant={isPopupMode ? 'body2' : 'h6'}
                              sx={{
                                 color: 'white',
                                 textAlign: 'center',
                                 px: 2,
                              }}
                           >
                              Loading video...
                           </Typography>
                        </Box>
                     )}
                  </Box>
               ) : (
                  // MAIN VIDEO: Show camera off message when no video and no screen share
                  <Box sx={{ textAlign: 'center' }}>
                     <VideocamOffIcon sx={{ fontSize: isPopupMode ? 40 : 60, mb: 2 }} />
                     <Typography variant={isPopupMode ? 'body2' : 'h6'}>
                        Your camera is off
                     </Typography>
                     {participants.length === 0 && (
                        <Typography
                           variant='body2'
                           sx={{ mt: 1, opacity: 0.7, fontSize: isPopupMode ? '11px' : '14px' }}
                        >
                           Waiting for others to join...
                        </Typography>
                     )}
                  </Box>
               )}
            </Box>

            {/* Hidden remote video container for video stream handling */}
            <Box
               ref={remoteVideoContainerRef}
               sx={{
                  position: 'absolute',
                  top: -1000, // Hide it off-screen
                  left: -1000,
                  width: 1,
                  height: 1,
                  overflow: 'hidden',
               }}
            />

            {/* Floating thumbnails overlay - only show if not in popup mode */}
            {!isPopupMode && renderFloatingThumbnails()}

            {/* CSS animation for speaking indicator */}
            <style>
               {`
                  @keyframes pulse {
                     0% { transform: scale(1); opacity: 1; }
                     50% { transform: scale(1.2); opacity: 0.7; }
                     100% { transform: scale(1); opacity: 1; }
                  }
               `}
            </style>
         </Box>
      );
   };

   // Render draggable, resizable thumbnails
   const renderFloatingThumbnails = () => {
      // THUMBNAILS: Always show camera feeds (person's face), never screen share content
      return Object.values(thumbnails).map((thumbnail) => {
         const participant = participants.find((p) => p.sid === thumbnail.id);
         const isLocalThumbnail = thumbnail.id === 'local-video';

         if (!participant && !isLocalThumbnail) return null;

         const displayName = isLocalThumbnail ? 'You' : participant?.identity || 'Unknown';

         return (
            <Box
               key={thumbnail.id}
               sx={{
                  position: 'absolute',
                  left: thumbnail.x,
                  top: thumbnail.y,
                  width: thumbnail.width,
                  height: thumbnail.height,
                  backgroundColor: '#333',
                  border: '2px solid #fff',
                  borderRadius: 2,
                  overflow: 'hidden',
                  zIndex: 9999, // Increased from 1000 to 9999 to ensure always on top
                  cursor: thumbnail.isDragging ? 'grabbing' : 'grab',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                  '&:hover': {
                     borderColor: '#2196f3',
                  },
               }}
            >
               {/* Video element - shows camera feed, not screen share */}
               <video
                  ref={(el) => {
                     if (el) {
                        thumbnailVideoRefs.current[thumbnail.id] = el;
                        // Video attachment will be handled by useEffect hooks to prevent infinite loops
                     }
                  }}
                  autoPlay
                  muted
                  playsInline
                  style={{
                     width: '100%',
                     height: '100%',
                     objectFit: 'cover',
                     transform: isLocalThumbnail ? 'scaleX(-1)' : 'none',
                  }}
               />

               {/* Thumbnail Loading Overlay */}
               {thumbnailLoadingStates[thumbnail.id] && (
                  <Box
                     sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000, // Increased to be even higher than thumbnail container
                     }}
                  >
                     <CircularProgress
                        sx={{
                           color: 'white',
                           mb: 1,
                        }}
                        size={24}
                     />
                     <Typography
                        variant='caption'
                        sx={{
                           color: 'white',
                           fontSize: '10px',
                           textAlign: 'center',
                        }}
                     >
                        Loading...
                     </Typography>
                  </Box>
               )}

               {/* Drag handle */}
               <Box
                  sx={{
                     position: 'absolute',
                     top: 0,
                     left: 0,
                     right: 0,
                     height: 30,
                     backgroundColor: 'rgba(0,0,0,0.7)',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'space-between',
                     px: 1,
                     cursor: 'grab',
                  }}
                  onMouseDown={(e) => {
                     e.preventDefault();
                     setDragState({
                        isDragging: true,
                        thumbnailId: thumbnail.id,
                        startX: e.clientX,
                        startY: e.clientY,
                        startThumbX: thumbnail.x,
                        startThumbY: thumbnail.y,
                     });

                     setThumbnails((prev) => ({
                        ...prev,
                        [thumbnail.id]: {
                           ...prev[thumbnail.id],
                           isDragging: true,
                        },
                     }));
                  }}
               >
                  <Typography
                     variant='caption'
                     sx={{
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: 500,
                        maxWidth: '60%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                     }}
                  >
                     {displayName}
                  </Typography>
                  <DragIcon sx={{ color: 'white', fontSize: 16 }} />
               </Box>

               {/* Resize handle */}
               <Box
                  sx={{
                     position: 'absolute',
                     bottom: 0,
                     right: 0,
                     width: 20,
                     height: 20,
                     backgroundColor: 'rgba(33, 150, 243, 0.8)',
                     cursor: 'se-resize',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                  }}
                  onMouseDown={(e) => {
                     e.preventDefault();
                     e.stopPropagation();

                     const startX = e.clientX;
                     const startY = e.clientY;
                     const startWidth = thumbnail.width;
                     const startHeight = thumbnail.height;

                     setThumbnails((prev) => ({
                        ...prev,
                        [thumbnail.id]: {
                           ...prev[thumbnail.id],
                           isResizing: true,
                        },
                     }));

                     const handleMouseMove = (e: MouseEvent) => {
                        const deltaX = e.clientX - startX;
                        const deltaY = e.clientY - startY;

                        setThumbnails((prev) => ({
                           ...prev,
                           [thumbnail.id]: {
                              ...prev[thumbnail.id],
                              width: Math.max(80, startWidth + deltaX),
                              height: Math.max(60, startHeight + deltaY),
                           },
                        }));
                     };

                     const handleMouseUp = () => {
                        setThumbnails((prev) => ({
                           ...prev,
                           [thumbnail.id]: {
                              ...prev[thumbnail.id],
                              isResizing: false,
                           },
                        }));

                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                     };

                     document.addEventListener('mousemove', handleMouseMove);
                     document.addEventListener('mouseup', handleMouseUp);
                  }}
               />

               {/* Speaking indicator */}
               {participant?.isDominantSpeaker && (
                  <Box
                     sx={{
                        position: 'absolute',
                        bottom: 4,
                        left: 4,
                        width: 8,
                        height: 8,
                        backgroundColor: '#4caf50',
                        borderRadius: '50%',
                        border: '1px solid white',
                        animation: 'pulse 1s infinite',
                     }}
                  />
               )}
            </Box>
         );
      });
   };

   const handleLeave = () => {
      // In popup mode, handle leave immediately (overlay handles confirmation)
      if (isPopupMode) {
         actuallyLeaveRoom();
         return;
      }

      // In full mode, show confirmation dialog first
      setShowLeaveConfirmation(true);
   };

   const handleConfirmLeave = () => {
      setShowLeaveConfirmation(false);
      actuallyLeaveRoom();
   };

   const handleCancelLeave = () => {
      setShowLeaveConfirmation(false);
   };

   const actuallyLeaveRoom = () => {
      // Stop local video track if it exists
      if (localVideoRef.current) {
         // Stop the video stream
         const stream = localVideoRef.current.srcObject as MediaStream;
         if (stream) {
            stream.getTracks().forEach((track) => {
               track.stop();
            });
         }
         // Clear the video element
         localVideoRef.current.srcObject = null;
      }

      // Stop any audio elements that might be playing
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach((audio) => {
         const stream = audio.srcObject as MediaStream;
         if (stream) {
            stream.getTracks().forEach((track) => {
               track.stop();
            });
         }
         audio.srcObject = null;
      });

      // Stop any remote video elements
      const videoElements = document.querySelectorAll('video:not([data-local])');
      videoElements.forEach((video) => {
         const stream = (video as HTMLVideoElement).srcObject as MediaStream;
         if (stream) {
            stream.getTracks().forEach((track) => {
               track.stop();
            });
         }
         (video as HTMLVideoElement).srcObject = null;
      });

      disconnectFromRoom();

      // Notify parent component that user is leaving
      onLeave?.();
   };

   const getNetworkQualityColor = (quality: typeof connectionQuality) => {
      switch (quality) {
         case 'excellent':
            return '#4caf50';
         case 'good':
            return '#8bc34a';
         case 'fair':
            return '#ff9800';
         case 'poor':
            return '#f44336';
         default:
            return '#9e9e9e';
      }
   };

   const renderParticipant = (participant: TwilioParticipant, isLocal: boolean = false) => (
      <Card
         key={participant.sid}
         sx={{
            mb: 1,
            border: participant.isDominantSpeaker ? '2px solid #2196f3' : '1px solid #e0e0e0',
            position: 'relative',
         }}
      >
         <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
               <Typography variant='body2' noWrap>
                  {participant.identity} {isLocal && '(You)'}
               </Typography>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton size='small' disabled>
                     {participant.audioTracks.length > 0 && participant.audioTracks[0].isEnabled ? (
                        <MicIcon fontSize='small' />
                     ) : (
                        <MicOffIcon fontSize='small' />
                     )}
                  </IconButton>
                  <IconButton size='small' disabled>
                     {participant.videoTracks.length > 0 && participant.videoTracks[0].isEnabled ? (
                        <VideocamIcon fontSize='small' />
                     ) : (
                        <VideocamOffIcon fontSize='small' />
                     )}
                  </IconButton>
                  {participant.networkQualityLevel !== undefined && (
                     <SignalIcon
                        fontSize='small'
                        sx={{
                           color: getNetworkQualityColor(
                              participant.networkQualityLevel >= 4
                                 ? 'excellent'
                                 : participant.networkQualityLevel >= 3
                                   ? 'good'
                                   : participant.networkQualityLevel >= 2
                                     ? 'fair'
                                     : 'poor'
                           ),
                        }}
                     />
                  )}
               </Box>
            </Box>
            {participant.isDominantSpeaker && (
               <Chip
                  label='Speaking'
                  size='small'
                  sx={{
                     position: 'absolute',
                     top: 4,
                     right: 4,
                     bgcolor: '#2196f3',
                     color: 'white',
                     fontSize: '0.6rem',
                     height: 20,
                  }}
               />
            )}
         </CardContent>
      </Card>
   );

   // Custom video toggle handler with enhanced debugging
   const handleVideoToggle = async () => {
      try {
         // Show loading state
         setIsMainVideoLoading(true);
         setLoadingMessage(isVideoEnabled ? 'Turning off camera...' : 'Turning on camera...');

         console.log(`Video toggle: ${isVideoEnabled ? 'disabling' : 'enabling'} camera`);

         // Store current screen sharing state to ensure it's not affected
         const currentScreenSharingState = {
            isScreenSharing,
            currentScreenSharer: currentScreenSharer?.identity,
            screenShareTrack,
         };

         // Call the original toggle function
         await toggleVideo();

         console.log('Video toggle completed successfully');

         // Monitor the results and ensure camera works properly
         setTimeout(() => {
            // Verify screen sharing state wasn't affected
            if (
               currentScreenSharingState.isScreenSharing &&
               currentScreenSharingState.currentScreenSharer
            ) {
               console.log('Verifying screen sharing state after video toggle...');

               // Screen sharing should still be active
               if (!isScreenSharing || !currentScreenSharer) {
                  console.warn(
                     'Screen sharing state was affected by video toggle - this should not happen'
                  );
               }
            }

            // If we just enabled video, add extra monitoring
            if (!isVideoEnabled && localVideoRef.current) {
               // !isVideoEnabled because state hasn't updated yet
               console.log('Video was enabled - checking video element...');

               const videoEl = localVideoRef.current;

               // If video element seems empty, try to help
               if (!videoEl.srcObject || videoEl.videoWidth === 0) {
                  console.log('Video element needs help after toggle - reattaching...');

                  // Force video element properties
                  videoEl.autoplay = true;
                  videoEl.playsInline = true;
                  videoEl.muted = true;

                  // Attempt to play
                  const playPromise = videoEl.play();
                  if (playPromise !== undefined) {
                     playPromise.catch((err) => {
                        // Only log if it's not an abort error from previous play attempts
                        if (err.name !== 'AbortError') {
                           console.log('Video play failed during toggle:', err);
                        }
                     });
                  }
               } else {
                  console.log('Video element is working properly after toggle');
               }
            }

            // Hide loading state
            setIsMainVideoLoading(false);
            setLoadingMessage('');
         }, 1000); // Reduced delay for faster response
      } catch (error) {
         console.error('Video toggle error:', error);

         // Hide loading state on error
         setIsMainVideoLoading(false);
         setLoadingMessage('');

         // Ensure local video continues working even if toggle failed
         if (localVideoRef.current && isVideoEnabled) {
            console.log('Video toggle failed - ensuring existing video continues working');

            setTimeout(() => {
               if (localVideoRef.current && isVideoEnabled) {
                  const videoEl = localVideoRef.current;

                  // Force video element properties
                  videoEl.autoplay = true;
                  videoEl.playsInline = true;
                  videoEl.muted = true;

                  // Attempt to play
                  const playPromise = videoEl.play();
                  if (playPromise !== undefined) {
                     playPromise.catch((err) => {
                        if (err.name !== 'AbortError') {
                           console.log('Video play failed during error recovery:', err);
                        }
                     });
                  }
               }
            }, 500);
         }
      }
   };

   // Cleanup effect for attachment protection
   useEffect(() => {
      return () => {
         // Reset attachment protection flag when component unmounts
         setAttachmentInProgress(false);
         if (attachmentTimeoutRef.current) {
            clearTimeout(attachmentTimeoutRef.current);
         }
      };
   }, []);

   // Reset attachment protection when screen sharing ends
   useEffect(() => {
      if (!currentScreenSharer) {
         setAttachmentInProgress(false);
         setIsScreenShareLoading(false);
         setLoadingMessage('');
         if (attachmentTimeoutRef.current) {
            clearTimeout(attachmentTimeoutRef.current);
         }
      }
   }, [currentScreenSharer]);

   // Additional cleanup effect to prevent stuck loading states
   useEffect(() => {
      // Clear loading states when screen sharing changes
      if (!currentScreenSharer) {
         setIsScreenShareLoading(false);
         setLoadingMessage('');
         // Also clear any stuck thumbnail loading states
         setThumbnailLoadingStates({});
      }
   }, [currentScreenSharer?.sid]);

   // Cleanup effect to prevent stuck loading states on unmount
   useEffect(() => {
      return () => {
         // Clear all loading states on unmount
         setIsScreenShareLoading(false);
         setIsMainVideoLoading(false);
         setIsUpdatingScreenShare(false);
         setLoadingMessage('');
         setThumbnailLoadingStates({});
      };
   }, []);

   // Effect to analyze and separate camera and screen share tracks
   useEffect(() => {
      console.log('[TRACK ANALYSIS TRIGGER] Participants changed, analyzing tracks:', {
         participantCount: participants.length,
         participantIdentities: participants.map((p) => p.identity),
         currentScreenSharer: currentScreenSharer?.identity,
      });

      const analyzeAndSeparateTracks = async () => {
         const newCameraTracksMap: { [participantId: string]: any } = {};
         const newScreenShareTracksMap: { [participantId: string]: any } = {};

         console.log(`[TRACK SEPARATION] Analyzing tracks for ${participants.length} participants`);

         // Process each participant's video tracks immediately
         for (const participant of participants) {
            if (participant.videoTracks && participant.videoTracks.length > 0) {
               console.log(
                  `[TRACK SEPARATION] Processing ${participant.videoTracks.length} tracks for ${participant.identity}`
               );

               for (let i = 0; i < participant.videoTracks.length; i++) {
                  const videoTrack = participant.videoTracks[i];

                  if (videoTrack && videoTrack.isEnabled) {
                     try {
                        // Create a temporary video element to analyze track
                        const tempVideo = document.createElement('video');
                        tempVideo.style.display = 'none';
                        tempVideo.style.position = 'absolute';
                        tempVideo.style.top = '-1000px';
                        document.body.appendChild(tempVideo);

                        // Attach track and wait for metadata
                        videoTrack.attach(tempVideo);
                        tempVideo.autoplay = true;
                        tempVideo.playsInline = true;
                        tempVideo.muted = true;

                        // Wait for video metadata to load with shorter timeout for faster response
                        await new Promise((resolve) => {
                           let attempts = 0;
                           const maxAttempts = 20; // Reduced from 40 to 20 (1 second timeout)

                           const checkMetadata = () => {
                              attempts++;
                              if (tempVideo.videoWidth > 0 && tempVideo.videoHeight > 0) {
                                 resolve(true);
                              } else if (attempts < maxAttempts) {
                                 setTimeout(checkMetadata, 50);
                              } else {
                                 console.log(
                                    `[TRACK SEPARATION] Timeout waiting for metadata for ${participant.identity} track ${i}`
                                 );
                                 resolve(false);
                              }
                           };
                           checkMetadata();
                        });

                        const videoWidth = tempVideo.videoWidth;
                        const videoHeight = tempVideo.videoHeight;
                        const trackName = videoTrack.name || '';

                        console.log(`[TRACK SEPARATION] Track ${i} for ${participant.identity}:`, {
                           width: videoWidth,
                           height: videoHeight,
                           name: trackName,
                        });

                        // Determine if this is a camera or screen share track
                        const isLargeResolution = videoWidth > 800 || videoHeight > 600;
                        const nameIndicatesScreenShare =
                           trackName.toLowerCase().includes('screen') ||
                           trackName.toLowerCase().includes('desktop') ||
                           trackName.toLowerCase().includes('window');
                        const nameIndicatesCamera =
                           trackName.toLowerCase().includes('camera') ||
                           trackName.toLowerCase().includes('user') ||
                           trackName.toLowerCase().includes('front') ||
                           trackName.toLowerCase().includes('back');

                        console.log(
                           `[TRACK SEPARATION] Analysis for ${participant.identity} track ${i}:`,
                           {
                              isLargeResolution,
                              nameIndicatesScreenShare,
                              nameIndicatesCamera,
                              trackName,
                              videoWidth,
                              videoHeight,
                              trackKind: videoTrack.kind,
                              participantSid: participant.sid,
                           }
                        );

                        if (
                           (isLargeResolution && !nameIndicatesCamera) ||
                           nameIndicatesScreenShare
                        ) {
                           console.log(
                              `[TRACK SEPARATION] Categorized as SCREEN SHARE track for ${participant.identity}`
                           );
                           newScreenShareTracksMap[participant.sid] = {
                              track: videoTrack,
                              width: videoWidth,
                              height: videoHeight,
                              name: trackName,
                              identity: participant.identity,
                           };
                        } else {
                           console.log(
                              `[TRACK SEPARATION] Categorized as CAMERA track for ${participant.identity}`
                           );
                           newCameraTracksMap[participant.sid] = {
                              track: videoTrack,
                              width: videoWidth,
                              height: videoHeight,
                              name: trackName,
                              identity: participant.identity,
                           };
                        }

                        // Clean up temp video
                        document.body.removeChild(tempVideo);
                     } catch (error) {
                        console.log(
                           `[TRACK SEPARATION] Failed to analyze track ${i} for ${participant.identity}:`,
                           error
                        );

                        // Fallback: assume it's a camera track
                        newCameraTracksMap[participant.sid] = {
                           track: videoTrack,
                           width: 0,
                           height: 0,
                           name: videoTrack.name || '',
                           identity: participant.identity,
                        };
                     }
                  }
               }
            }
         }

         // Update the state with separated tracks
         console.log('[TRACK MAPS UPDATE]', {
            newCameraTracksCount: Object.keys(newCameraTracksMap).length,
            newScreenShareTracksCount: Object.keys(newScreenShareTracksMap).length,
            cameraParticipants: Object.keys(newCameraTracksMap),
            screenShareParticipants: Object.keys(newScreenShareTracksMap),
         });

         setCameraTracksMap(newCameraTracksMap);
         setScreenShareTracksMap(newScreenShareTracksMap);

         console.log(`[TRACK SEPARATION] Separated tracks:`, {
            cameraTracksCount: Object.keys(newCameraTracksMap).length,
            screenShareTracksCount: Object.keys(newScreenShareTracksMap).length,
         });
      };

      if (participants.length > 0) {
         // Run immediately without delay for faster thumbnail creation
         analyzeAndSeparateTracks();
      }
   }, [participants]);

   // Effect to smoothly prioritize screen sharing when track arrays change
   useEffect(() => {
      // Only run when we have meaningful changes in track arrays
      const hasScreenShareTracks = Object.keys(screenShareTracksMap).length > 0;
      const hasCameraTracks = Object.keys(cameraTracksMap).length > 0;

      if (!hasScreenShareTracks && !hasCameraTracks) {
         return; // No tracks to process
      }

      console.log(`[TRACK PRIORITY] Checking video priority:`, {
         screenShareTracks: Object.keys(screenShareTracksMap).length,
         cameraTracks: Object.keys(cameraTracksMap).length,
         currentScreenSharer: currentScreenSharer?.identity,
         isLocalScreenSharing: currentScreenSharer?.sid === localParticipant?.sid,
      });

      // CRITICAL: If we have screen share tracks, ensure screen sharing is ALWAYS prioritized
      if (hasScreenShareTracks && currentScreenSharer) {
         console.log(
            `[TRACK PRIORITY] Screen sharing detected, prioritizing immediately for ${currentScreenSharer.identity}`
         );

         // GUARD: If screen sharing is active, NEVER allow main video to show anything else
         const mainVideo = document.querySelector('[data-testid="main-video"]') as HTMLVideoElement;
         if (mainVideo) {
            const currentPriority = mainVideo.getAttribute('data-priority');
            if (currentPriority !== 'screen-share') {
               console.log(
                  `[SCREEN SHARE GUARD] Main video showing ${currentPriority}, forcing screen share priority`
               );
            }
         }

         // Add debouncing to prevent rapid updates when track arrays change
         const debounceTimer = setTimeout(() => {
            const mainVideo = document.querySelector(
               '[data-testid="main-video"]'
            ) as HTMLVideoElement;

            if (mainVideo) {
               const currentPriority = mainVideo.getAttribute('data-priority');
               const currentScreenSharerAttr = mainVideo.getAttribute('data-screen-sharer');

               // Check if we have the expected screen share track
               let screenShareTrack = screenShareTracksMap[currentScreenSharer.sid];

               // SPECIAL CASE: If this is the local user screen sharing, always show it
               const isLocalScreenSharing = currentScreenSharer.sid === localParticipant?.sid;

               // For local screen sharing, get the track from VideoCallContext if not in map
               if (isLocalScreenSharing && !screenShareTrack && localScreenShareTrack) {
                  console.log(
                     `[LOCAL SCREEN SHARE] Using local screen share track from VideoCallContext`
                  );
                  screenShareTrack = {
                     track: localScreenShareTrack,
                     identity: localParticipant?.identity,
                     name: 'screen-share',
                  };
               }

               // CRITICAL: Screen share should ALWAYS be shown in main video, never camera feed
               // Use proper stream comparison instead of dimensions
               let shouldShowScreenShare = false;

               if (isLocalScreenSharing) {
                  console.log(
                     `[LOCAL SCREEN SHARE] Local user is screen sharing, forcing main video display`
                  );
                  shouldShowScreenShare = true;
               }

               if (screenShareTrack && screenShareTrack.track && screenShareTrack.track.isEnabled) {
                  // Check if main video is showing the wrong content
                  shouldShowScreenShare =
                     currentPriority !== 'screen-share' ||
                     currentScreenSharerAttr !== currentScreenSharer.identity;

                  // Additional check: compare with expected screen share stream (only if needed)
                  if (!shouldShowScreenShare && mainVideo.srcObject) {
                     // Create a temp video to get the expected screen share stream
                     const tempVideo = document.createElement('video');
                     tempVideo.style.display = 'none';
                     tempVideo.style.position = 'absolute';
                     tempVideo.style.top = '-1000px';
                     document.body.appendChild(tempVideo);

                     try {
                        screenShareTrack.track.attach(tempVideo);

                        // Compare streams - if different, we need to update
                        if (tempVideo.srcObject && mainVideo.srcObject !== tempVideo.srcObject) {
                           shouldShowScreenShare = true;
                        }
                     } catch (error) {
                        console.log(`[TRACK PRIORITY] Error comparing streams:`, error);
                     }

                     document.body.removeChild(tempVideo);
                  }
               }

               if (shouldShowScreenShare) {
                  console.log(
                     `[TRACK PRIORITY] Screen sharing should be prioritized for ${currentScreenSharer.identity} (current priority: ${currentPriority})`
                  );

                  if (
                     screenShareTrack &&
                     screenShareTrack.track &&
                     screenShareTrack.track.isEnabled
                  ) {
                     console.log(
                        `[TRACK PRIORITY] Screen share track ready - gently updating screen share display`
                     );

                     // Prevent overlapping updates
                     if (isUpdatingScreenShare) {
                        console.log(
                           `[TRACK PRIORITY] Screen share update already in progress, skipping`
                        );
                        return;
                     }

                     // Gentle priority enforcement with longer delays
                     const enforceScreenSharePriority = () => {
                        try {
                           setIsUpdatingScreenShare(true);

                           // Create temp video to get stream
                           const tempVideo = document.createElement('video');
                           tempVideo.style.display = 'none';
                           tempVideo.style.position = 'absolute';
                           tempVideo.style.top = '-1000px';
                           document.body.appendChild(tempVideo);

                           screenShareTrack.track.attach(tempVideo);
                           tempVideo.autoplay = true;
                           tempVideo.playsInline = true;
                           tempVideo.muted = false;

                           setTimeout(() => {
                              if (tempVideo.srcObject) {
                                 console.log(
                                    `[TRACK PRIORITY] Gently updating main video to show screen share`
                                 );

                                 // Gentle screen share display
                                 mainVideo.srcObject = tempVideo.srcObject;
                                 mainVideo.setAttribute('data-priority', 'screen-share');
                                 mainVideo.setAttribute(
                                    'data-screen-sharer',
                                    currentScreenSharer.identity
                                 );

                                 // Ensure playback
                                 const playPromise = mainVideo.play();
                                 if (playPromise !== undefined) {
                                    playPromise.catch(() => {});
                                 }
                              }

                              // Clean up
                              document.body.removeChild(tempVideo);
                              setIsUpdatingScreenShare(false);
                           }, 300); // Longer delay to prevent flashing
                        } catch (error) {
                           console.log(
                              `[TRACK PRIORITY] Error during gentle screen share enforcement:`,
                              error
                           );
                           setIsUpdatingScreenShare(false);
                        }
                     };

                     // Execute with debouncing
                     enforceScreenSharePriority();
                  }
               } else {
                  console.log(
                     `[TRACK PRIORITY] Screen sharing already correctly prioritized for ${currentScreenSharer.identity}`
                  );
               }
            }
         }, 800); // Debounce delay to prevent rapid updates when track arrays change

         return () => clearTimeout(debounceTimer);
      }
   }, [
      screenShareTracksMap,
      cameraTracksMap,
      currentScreenSharer?.identity,
      currentScreenSharer?.sid,
      localParticipant?.sid,
   ]);

   // Additional effect specifically for screen sharer camera toggle protection
   useEffect(() => {
      // CRITICAL: When screen sharer toggles camera, ensure main video NEVER shows their camera
      if (currentScreenSharer && currentScreenSharer.sid === localParticipant?.sid) {
         console.log(
            `[SCREEN SHARER PROTECTION] Monitoring camera toggles for screen sharer ${currentScreenSharer.identity}`
         );

         let lastUpdateTime = 0;
         const UPDATE_COOLDOWN = 2000; // 2 seconds cooldown between updates

         // Set up gentler monitoring specifically for screen sharer's camera toggles
         const screenSharerProtectionInterval = setInterval(() => {
            const now = Date.now();

            // Skip if we recently updated to prevent flashing
            if (now - lastUpdateTime < UPDATE_COOLDOWN) {
               return;
            }

            const mainVideo = document.querySelector(
               '[data-testid="main-video"]'
            ) as HTMLVideoElement;

            if (mainVideo) {
               const currentPriority = mainVideo.getAttribute('data-priority');
               const currentScreenSharerAttr = mainVideo.getAttribute('data-screen-sharer');

               // Check if we have the expected screen share track
               const screenShareTrack = screenShareTracksMap[currentScreenSharer.sid];

               // Use proper stream comparison instead of dimensions
               let isShowingCamera = false;

               if (screenShareTrack && screenShareTrack.track && screenShareTrack.track.isEnabled) {
                  // Check if main video is showing the wrong content
                  isShowingCamera =
                     currentPriority !== 'screen-share' ||
                     currentScreenSharerAttr !== currentScreenSharer.identity;

                  // Additional check: compare with expected screen share stream (only if priority check failed)
                  if (!isShowingCamera && mainVideo.srcObject) {
                     // Create a temp video to get the expected screen share stream
                     const tempVideo = document.createElement('video');
                     tempVideo.style.display = 'none';
                     tempVideo.style.position = 'absolute';
                     tempVideo.style.top = '-1000px';
                     document.body.appendChild(tempVideo);

                     try {
                        screenShareTrack.track.attach(tempVideo);

                        // Compare streams - if different, we're showing camera
                        if (tempVideo.srcObject && mainVideo.srcObject !== tempVideo.srcObject) {
                           isShowingCamera = true;
                        }
                     } catch (error) {
                        console.log(`[SCREEN SHARER PROTECTION] Error comparing streams:`, error);
                     }

                     document.body.removeChild(tempVideo);
                  }
               }

               if (isShowingCamera) {
                  console.warn(
                     `[SCREEN SHARER PROTECTION] Main video showing camera instead of screen share - gentle fix`
                  );

                  if (
                     screenShareTrack &&
                     screenShareTrack.track &&
                     screenShareTrack.track.isEnabled
                  ) {
                     console.log(
                        `[SCREEN SHARER PROTECTION] Gently restoring screen share to main video`
                     );

                     // Prevent overlapping updates
                     if (isUpdatingScreenShare) {
                        console.log(
                           `[SCREEN SHARER PROTECTION] Screen share update already in progress, skipping`
                        );
                        return;
                     }

                     // Update last update time to prevent rapid updates
                     lastUpdateTime = now;

                     // Gentle restoration with longer timeout
                     try {
                        setIsUpdatingScreenShare(true);

                        const tempVideo = document.createElement('video');
                        tempVideo.style.display = 'none';
                        tempVideo.style.position = 'absolute';
                        tempVideo.style.top = '-1000px';
                        document.body.appendChild(tempVideo);

                        screenShareTrack.track.attach(tempVideo);
                        tempVideo.autoplay = true;
                        tempVideo.playsInline = true;
                        tempVideo.muted = false;

                        setTimeout(() => {
                           if (tempVideo.srcObject) {
                              mainVideo.srcObject = tempVideo.srcObject;
                              mainVideo.setAttribute('data-priority', 'screen-share');
                              mainVideo.setAttribute(
                                 'data-screen-sharer',
                                 currentScreenSharer.identity
                              );
                              mainVideo.play().catch(() => {});
                           }
                           document.body.removeChild(tempVideo);
                           setIsUpdatingScreenShare(false);
                        }, 200); // Gentler response to prevent flashing
                     } catch (error) {
                        console.log(`[SCREEN SHARER PROTECTION] Error during gentle fix:`, error);
                        setIsUpdatingScreenShare(false);
                     }
                  }
               }
            }
         }, 500); // Reduced frequency from 100ms to 500ms to prevent flashing

         return () => clearInterval(screenSharerProtectionInterval);
      }
   }, [currentScreenSharer?.sid, localParticipant?.sid, screenShareTracksMap, cameraTracksMap]);

   // Reset screen share attached state when screen sharing ends or changes
   useEffect(() => {
      if (!currentScreenSharer) {
         setIsScreenShareAttached(false);
         setIsUpdatingScreenShare(false); // Reset update state when screen sharing ends
         prevScreenSharerRef.current = null;
      } else if (currentScreenSharer.sid !== prevScreenSharerRef.current) {
         setIsScreenShareAttached(false);
         setIsUpdatingScreenShare(false); // Reset update state when screen sharer changes
         prevScreenSharerRef.current = currentScreenSharer.sid;
      }
   }, [currentScreenSharer]);

   // Effect to immediately handle screen sharing changes from VideoCallContext
   useEffect(() => {
      if (currentScreenSharer) {
         console.log(
            `[IMMEDIATE SCREEN SHARE] Screen sharing detected from ${currentScreenSharer.identity}, forcing track analysis`
         );

         // Force immediate track analysis for ANY screen sharing (local or remote)
         setTimeout(() => {
            // This will trigger the track analysis useEffect
            setThumbnails((prev) => ({ ...prev }));
         }, 100);

         // Also force a more aggressive update after a short delay
         setTimeout(() => {
            console.log(
               `[AGGRESSIVE UPDATE] Forcing aggressive track analysis for ${currentScreenSharer.identity}`
            );
            // Force track analysis by updating a dummy state that triggers the useEffect
            setAttachedThumbnails((prev) => new Set(Array.from(prev)));
         }, 500);
      }
   }, [currentScreenSharer?.sid]);

   // Effect to AGGRESSIVELY maintain screen sharing display (runs on every state change)
   useEffect(() => {
      if (
         currentScreenSharer &&
         (screenShareTracksMap[currentScreenSharer.sid] || localScreenShareTrack)
      ) {
         const mainVideo = document.querySelector('[data-testid="main-video"]') as HTMLVideoElement;
         if (mainVideo) {
            const currentPriority = mainVideo.getAttribute('data-priority');
            const currentScreenSharer_attr = mainVideo.getAttribute('data-screen-sharer');

            // If main video is not showing screen share, force it immediately
            if (
               currentPriority !== 'screen-share' ||
               currentScreenSharer_attr !== currentScreenSharer.sid
            ) {
               console.log(
                  `[AGGRESSIVE SCREEN SHARE GUARD] Main video not showing screen share (${currentPriority}), forcing immediate attachment`
               );

               // Force immediate screen share attachment
               setTimeout(async () => {
                  const success = await attachScreenShareToMain();
                  if (success) {
                     console.log(`[AGGRESSIVE GUARD] Successfully forced screen share display`);
                  } else {
                     console.log(`[AGGRESSIVE GUARD] Failed to force screen share display`);
                  }
               }, 50); // Very short delay
            }
         }
      }
   }, [currentScreenSharer, screenShareTracksMap, localScreenShareTrack, participants.length]);

   // Effect to handle thumbnail video attachment (moved from ref callback to prevent infinite loops)
   useEffect(() => {
      Object.keys(thumbnailVideoRefs.current).forEach((thumbnailId) => {
         const thumbnailEl = thumbnailVideoRefs.current[thumbnailId];
         if (!thumbnailEl) return;

         // Skip if already attached
         if (attachedThumbnails.has(thumbnailId) && thumbnailEl.srcObject) {
            return;
         }

         // Skip if currently loading
         if (thumbnailLoadingStates[thumbnailId]) {
            return;
         }

         // Handle local video thumbnail
         if (thumbnailId === 'local-video') {
            if (localVideoRef.current && localVideoRef.current.srcObject) {
               const localStream = localVideoRef.current.srcObject as MediaStream;
               if (localStream && thumbnailEl.srcObject !== localStream) {
                  thumbnailEl.srcObject = localStream;
                  thumbnailEl.play().catch(() => {});
                  setAttachedThumbnails((prev) => new Set(prev).add(thumbnailId));
               }
            }
            return;
         }

         // Handle remote participant thumbnails
         const cameraTrackInfo = cameraTracksMap[thumbnailId];
         if (cameraTrackInfo && cameraTrackInfo.track && cameraTrackInfo.track.isEnabled) {
            if (!thumbnailEl.srcObject && !attachedThumbnails.has(thumbnailId)) {
               try {
                  cameraTrackInfo.track.attach(thumbnailEl);
                  thumbnailEl.play().catch(() => {});
                  setAttachedThumbnails((prev) => new Set(prev).add(thumbnailId));
               } catch (error) {
                  console.warn(`Failed to attach video in useEffect:`, error);
               }
            }
         }
      });
   }, [cameraTracksMap, attachedThumbnails, thumbnailLoadingStates, localVideoRef]);

   const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
         return;
      }
      setToastOpen(false);
   };

   if (isConnecting) {
      return (
         <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>Connecting to video room...</Typography>
         </Box>
      );
   }

   // Handle connection errors as toast instead of blocking the UI
   if (error && !error.toLowerCase().includes('screen sharing')) {
      // Show toast for non-screen sharing errors
      if (!toastOpen) {
         setToastMessage(error);
         setToastSeverity('error');
         setToastOpen(true);
      }
   }

   return (
      <Box
         sx={{
            height: isPopupMode ? '100%' : 600,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'white',
         }}
      >
         {/* Header - Only show in full screen mode */}
         {!isPopupMode && (
            <Box
               sx={{
                  p: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
               }}
            >
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant='h6'>{eventTitle}</Typography>
                  {enableRecording && (
                     <Chip icon={<RecordIcon />} label='Recording' color='error' size='small' />
                  )}
                  <Chip
                     label={`${participants.length + 1} participant${participants.length !== 0 ? 's' : ''}`}
                     size='small'
                     variant='outlined'
                  />
               </Box>

               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SignalIcon sx={{ color: getNetworkQualityColor(connectionQuality) }} />
                  <Typography variant='caption' sx={{ textTransform: 'capitalize' }}>
                     {connectionQuality}
                  </Typography>
               </Box>
            </Box>
         )}

         {/* Main content area */}
         <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {/* Video area */}
            <Box sx={{ flex: 1, position: 'relative', backgroundColor: '#000' }}>
               {/* Main video */}
               <Box
                  sx={{
                     position: 'absolute',
                     top: 0,
                     left: 0,
                     right: 0,
                     bottom: isPopupMode ? 60 : 80,
                  }}
               >
                  {renderMainVideo()}
               </Box>

               {/* Controls */}
               <Box
                  sx={{
                     position: 'absolute',
                     bottom: 0,
                     left: 0,
                     right: 0,
                     height: isPopupMode ? 60 : 80,
                     backgroundColor: 'rgba(0,0,0,0.8)',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                  }}
               >
                  <Stack direction='row' spacing={isPopupMode ? 0.5 : 1}>
                     <Tooltip title={isAudioEnabled ? 'Mute microphone' : 'Unmute microphone'}>
                        <IconButton
                           color={isAudioEnabled ? 'primary' : 'default'}
                           onClick={toggleAudio}
                           disabled={!isConnected}
                           size={isPopupMode ? 'small' : 'medium'}
                           sx={{
                              backgroundColor: isAudioEnabled
                                 ? 'rgba(25, 118, 210, 0.8)'
                                 : 'rgba(255, 255, 255, 0.1)',
                              color: 'white',
                              backdropFilter: 'blur(10px)',
                              border: isAudioEnabled
                                 ? '1px solid rgba(25, 118, 210, 0.3)'
                                 : '1px solid rgba(255, 255, 255, 0.2)',
                              '&:hover': {
                                 backgroundColor: isAudioEnabled
                                    ? 'rgba(25, 118, 210, 0.9)'
                                    : 'rgba(255, 255, 255, 0.2)',
                                 transform: 'translateY(-1px)',
                                 boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                              },
                           }}
                        >
                           {isAudioEnabled ? <MicIcon /> : <MicOffIcon />}
                        </IconButton>
                     </Tooltip>

                     <Tooltip title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}>
                        <IconButton
                           color={isVideoEnabled ? 'primary' : 'default'}
                           onClick={handleVideoToggle}
                           disabled={!isConnected}
                           size={isPopupMode ? 'small' : 'medium'}
                           sx={{
                              backgroundColor: isVideoEnabled
                                 ? 'rgba(25, 118, 210, 0.8)'
                                 : 'rgba(255, 255, 255, 0.1)',
                              color: 'white',
                              backdropFilter: 'blur(10px)',
                              border: isVideoEnabled
                                 ? '1px solid rgba(25, 118, 210, 0.3)'
                                 : '1px solid rgba(255, 255, 255, 0.2)',
                              '&:hover': {
                                 backgroundColor: isVideoEnabled
                                    ? 'rgba(25, 118, 210, 0.9)'
                                    : 'rgba(255, 255, 255, 0.2)',
                                 transform: 'translateY(-1px)',
                                 boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                              },
                           }}
                        >
                           {isVideoEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
                        </IconButton>
                     </Tooltip>

                     <Tooltip
                        title={
                           currentScreenSharer && currentScreenSharer.sid !== localParticipant?.sid
                              ? `${currentScreenSharer.identity} is already sharing their screen. Only one person can share at a time.`
                              : isScreenSharing
                                ? 'Stop screen sharing'
                                : 'Share screen'
                        }
                     >
                        <IconButton
                           color={isScreenSharing ? 'primary' : 'default'}
                           onClick={toggleScreenShare}
                           disabled={
                              !isConnected ||
                              Boolean(
                                 currentScreenSharer &&
                                    currentScreenSharer.sid !== localParticipant?.sid
                              )
                           }
                           size={isPopupMode ? 'small' : 'medium'}
                           sx={{
                              backgroundColor: isScreenSharing
                                 ? 'rgba(25, 118, 210, 0.8)'
                                 : currentScreenSharer &&
                                     currentScreenSharer.sid !== localParticipant?.sid
                                   ? 'rgba(255, 255, 255, 0.05)'
                                   : 'rgba(255, 255, 255, 0.1)',
                              color: 'white',
                              backdropFilter: 'blur(10px)',
                              border: isScreenSharing
                                 ? '1px solid rgba(25, 118, 210, 0.3)'
                                 : '1px solid rgba(255, 255, 255, 0.2)',
                              opacity:
                                 currentScreenSharer &&
                                 currentScreenSharer.sid !== localParticipant?.sid
                                    ? 0.5
                                    : 1,
                              '&:hover': {
                                 backgroundColor: isScreenSharing
                                    ? 'rgba(25, 118, 210, 0.9)'
                                    : currentScreenSharer &&
                                        currentScreenSharer.sid !== localParticipant?.sid
                                      ? 'rgba(255, 255, 255, 0.05)'
                                      : 'rgba(255, 255, 255, 0.2)',
                                 transform:
                                    isScreenSharing ||
                                    !(
                                       currentScreenSharer &&
                                       currentScreenSharer.sid !== localParticipant?.sid
                                    )
                                       ? 'translateY(-1px)'
                                       : 'none',
                                 boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                              },
                              '&:disabled': {
                                 backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                 color: 'rgba(255, 255, 255, 0.3)',
                                 border: '1px solid rgba(255, 255, 255, 0.1)',
                              },
                           }}
                        >
                           {isScreenSharing ? <StopScreenShareIcon /> : <ScreenShareIcon />}
                        </IconButton>
                     </Tooltip>

                     <Divider
                        orientation='vertical'
                        flexItem
                        sx={{ mx: 1, backgroundColor: 'grey.600' }}
                     />

                     {showParticipants && (
                        <Tooltip title='Toggle participants'>
                           <IconButton
                              onClick={() => setShowParticipantsList(!showParticipantsList)}
                              size={isPopupMode ? 'small' : 'medium'}
                              sx={{
                                 backgroundColor: showParticipantsList
                                    ? 'rgba(25, 118, 210, 0.8)'
                                    : 'rgba(255, 255, 255, 0.1)',
                                 color: 'white',
                                 backdropFilter: 'blur(10px)',
                                 border: showParticipantsList
                                    ? '1px solid rgba(25, 118, 210, 0.3)'
                                    : '1px solid rgba(255, 255, 255, 0.2)',
                                 '&:hover': {
                                    backgroundColor: showParticipantsList
                                       ? 'rgba(25, 118, 210, 0.9)'
                                       : 'rgba(255, 255, 255, 0.2)',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                 },
                              }}
                           >
                              <Badge badgeContent={participants.length + 1} color='secondary'>
                                 <PeopleIcon />
                              </Badge>
                           </IconButton>
                        </Tooltip>
                     )}

                     <Divider
                        orientation='vertical'
                        flexItem
                        sx={{ mx: 1, backgroundColor: 'grey.600' }}
                     />

                     <Tooltip title='Leave meeting'>
                        <IconButton
                           onClick={handleLeave}
                           size={isPopupMode ? 'small' : 'medium'}
                           sx={{
                              backgroundColor: 'rgba(211, 47, 47, 0.8)',
                              color: 'white',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(211, 47, 47, 0.3)',
                              '&:hover': {
                                 backgroundColor: 'rgba(211, 47, 47, 0.9)',
                                 transform: 'translateY(-1px)',
                                 boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                              },
                           }}
                        >
                           <CallEndIcon />
                        </IconButton>
                     </Tooltip>
                  </Stack>
               </Box>
            </Box>

            {/* Side panels */}
            {showParticipantsList && (
               <Box
                  sx={{
                     width: 300,
                     borderLeft: 1,
                     borderColor: 'divider',
                     backgroundColor: 'background.paper',
                     position: 'absolute',
                     top: 0,
                     right: 0,
                     bottom: 0,
                     zIndex: 10,
                  }}
               >
                  <Box sx={{ p: 2 }}>
                     <Typography variant='h6' gutterBottom>
                        Participants ({participants.length + 1})
                     </Typography>
                     {localParticipant && renderParticipant(localParticipant, true)}
                     {participants.map((participant) => renderParticipant(participant))}
                  </Box>
               </Box>
            )}
         </Box>

         {/* Leave Confirmation Dialog */}
         <Dialog open={showLeaveConfirmation} onClose={handleCancelLeave}>
            <DialogTitle>Leave Meeting</DialogTitle>
            <DialogContent>
               <DialogContentText>
                  Are you sure you want to leave the meeting? This action cannot be undone.
               </DialogContentText>
            </DialogContent>
            <DialogActions>
               <Button onClick={handleCancelLeave} color='primary'>
                  Cancel
               </Button>
               <Button onClick={handleConfirmLeave} color='error' variant='contained'>
                  Leave
               </Button>
            </DialogActions>
         </Dialog>

         {/* Toast Notification */}
         <Snackbar
            open={toastOpen}
            autoHideDuration={6000}
            onClose={handleToastClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
         >
            <MuiAlert
               onClose={handleToastClose}
               severity={toastSeverity}
               sx={{ width: '100%' }}
               variant='filled'
            >
               {toastMessage}
            </MuiAlert>
         </Snackbar>
      </Box>
   );
};

export default TwilioVideoRoom;
